import { ClipboardDocumentIcon } from "@heroicons/react/20/solid";
import { useFormikContext } from "formik";
import type React from "react";
import { useState } from "react";
import NewTextFormComponent from "../../../components/Forms/NewTextFormComponent";
import { SelectComboField } from "../../../components/Forms/SelectCombo/SelectCombo";
import { Button } from "../../../components/Theme/button";

interface GenericCSVConfigEditorProps {
  namespace: string;
  disabled?: boolean;
}

const TIMEZONE_OPTIONS = [
  "UTC",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Zurich",
  "Europe/Helsinki",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Vienna",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Lisbon",
  "Europe/Athens",
  "Europe/Bucharest",
  "Europe/Istanbul",
  "Europe/Moscow",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
].map((tz) => ({ value: tz, label: tz }));

interface ConfigFormValues {
  [key: string]: {
    columns: {
      timestampColumn: string;
      identifierColumn: string;
      valueColumn: string;
    };
    timestamp: {
      format: string;
      timezone: string;
    };
  };
}

export default function GenericCSVConfigEditor(
  props: GenericCSVConfigEditorProps,
): React.ReactElement {
  const { namespace, disabled = false } = props;
  const { values } = useFormikContext<ConfigFormValues>();
  const [isCopied, setIsCopied] = useState(false);

  const config = values[namespace];

  const handleCopyAsJSON = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const snakeCaseConfig = {
        columns: {
          timestamp_column: config?.columns?.timestampColumn || "",
          identifier_column: config?.columns?.identifierColumn || "",
          value_column: config?.columns?.valueColumn || "",
        },
        timestamp: {
          format: config?.timestamp?.format || null,
          timezone: config?.timestamp?.timezone || "UTC",
        },
      };
      await navigator.clipboard.writeText(
        JSON.stringify(snakeCaseConfig, null, 2),
      );
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-zinc-950 dark:text-white">
          Generic CSV Configuration
        </h3>
        <span className="relative inline-block">
          <Button
            type="button"
            outline
            size="sm"
            onClick={handleCopyAsJSON}
            disabled={disabled}
          >
            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
            Copy as JSON
          </Button>
          {isCopied && (
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap dark:bg-zinc-700">
              Copied!
            </div>
          )}
        </span>
      </div>

      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Configure which CSV columns map to timestamp, identifier, and value.
        Per-asset field mappings are configured on each child asset position.
      </p>

      {/* Column Configuration */}
      <fieldset className="border border-zinc-950/10 dark:border-white/10 rounded-lg p-4">
        <legend className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 px-2">
          Column Configuration
        </legend>
        <NewTextFormComponent
          fieldName={`${namespace}.columns.timestampColumn`}
          label={{ label: "Timestamp Column" }}
          placeholder="e.g. Timestamp"
          disabled={disabled}
        />
        <NewTextFormComponent
          fieldName={`${namespace}.columns.identifierColumn`}
          label={{ label: "Identifier Column" }}
          placeholder="e.g. Meter"
          disabled={disabled}
        />
        <NewTextFormComponent
          fieldName={`${namespace}.columns.valueColumn`}
          label={{ label: "Value Column" }}
          placeholder="e.g. Value"
          disabled={disabled}
        />
      </fieldset>

      {/* Timestamp Configuration */}
      <fieldset className="border border-zinc-950/10 dark:border-white/10 rounded-lg p-4">
        <legend className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 px-2">
          Timestamp Configuration
        </legend>
        <NewTextFormComponent
          fieldName={`${namespace}.timestamp.format`}
          label={{
            label: "Format (optional)",
            description:
              "Leave empty for auto-detection. Set explicitly for ambiguous dates " +
              "(e.g. 01/02/2026 could be Jan 2 or Feb 1). " +
              "Tokens: YYYY (year), MM (month), DD (day), HH (hour), mm (minute), ss (second). " +
              "Examples: DD-MM-YYYY HH:mm, YYYY-MM-DD HH:mm:ss, DD/MM/YYYY HH:mm",
          }}
          placeholder="Auto-detect if empty, e.g. DD-MM-YYYY HH:mm"
          disabled={disabled}
        />
        <SelectComboField
          fieldName={`${namespace}.timestamp.timezone`}
          label={{ label: "Timezone" }}
          options={TIMEZONE_OPTIONS}
          disabled={disabled}
        />
      </fieldset>
    </div>
  );
}
